/**
 * CartSidebar - Slide-out cart panel
 * Amazon-style layout: quantity dropdown, delete near quantity
 */
import React, { useEffect } from 'react';
import { COLORS, FONT_FAMILY, SHOP_SPACING } from '../ShopStyles';
import { useCart } from '../context/CartContext';

const CartSidebar = ({ onCheckout }) => {
  const {
    items,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity,
    totalItems,
    subtotal,
    shipping,
    total,
  } = useCart();

  // Inject animations
  useEffect(() => {
    if (!isOpen) return;
    
    const styleSheet = document.createElement('style');
    styleSheet.id = 'cart-sidebar-animations';
    styleSheet.textContent = `
      @keyframes slideInCart {
        from { transform: translateX(100%); }
        to { transform: translateX(0); }
      }
      @keyframes fadeInOverlay {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes bounceIn {
        0% { transform: scale(0.95); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
      .cart-sidebar {
        animation: slideInCart 0.3s ease-out;
      }
      .cart-overlay {
        animation: fadeInOverlay 0.2s ease-out;
      }
      .cart-item {
        animation: bounceIn 0.3s ease-out;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      const existing = document.getElementById('cart-sidebar-animations');
      if (existing) existing.remove();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Calculate free shipping progress
  const freeShippingThreshold = 50;
  const progressToFreeShipping = Math.min((subtotal / freeShippingThreshold) * 100, 100);
  const amountToFreeShipping = Math.max(freeShippingThreshold - subtotal, 0);

  return (
    <>
      {/* Overlay */}
      <div className="cart-overlay" style={styles.overlay} onClick={closeCart} />
      
      {/* Sidebar */}
      <div className="cart-sidebar" style={styles.sidebar}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerEmoji}>🛒</span>
            <div>
              <h2 style={styles.title}>Your Cart</h2>
              {totalItems > 0 && (
                <p style={styles.itemCountText}>{totalItems} item{totalItems !== 1 ? 's' : ''}</p>
              )}
            </div>
          </div>
          <button onClick={closeCart} style={styles.closeButton} aria-label="Close cart">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ display: 'block' }}>
              <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Empty State */}
        {items.length === 0 && (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>🛒</span>
            <h3 style={styles.emptyTitle}>Your cart is empty!</h3>
            <p style={styles.emptySubtext}>
              Time to find some paw-some products! 🐾
            </p>
            <button onClick={closeCart} style={styles.shopNowBtn}>
              🛍️ Start Shopping
            </button>
          </div>
        )}

        {/* Cart Items */}
        {items.length > 0 && (
          <>
            {/* Free Shipping Progress */}
            {subtotal < freeShippingThreshold && (
              <div style={styles.freeShippingBanner}>
                <div style={styles.progressBarWrapper}>
                  <div style={{...styles.progressBarFill, width: `${progressToFreeShipping}%`}} />
                </div>
                <p style={styles.freeShippingText}>
                  🚚 Add <strong>${amountToFreeShipping.toFixed(2)}</strong> more for <strong>FREE shipping!</strong>
                </p>
              </div>
            )}
            
            {subtotal >= freeShippingThreshold && (
              <div style={styles.freeShippingUnlocked}>
                🎉 You unlocked <strong>FREE shipping!</strong> 🎉
              </div>
            )}

            <div style={styles.itemList}>
              {items.map((item, index) => (
                <CartItem
                  key={item.cartId}
                  item={item}
                  index={index}
                  onRemove={() => removeItem(item.cartId)}
                  onUpdateQuantity={(qty) => updateQuantity(item.cartId, qty)}
                />
              ))}
            </div>

            {/* Summary */}
            <div style={styles.summary}>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Subtotal</span>
                <span style={styles.summaryValue}>${subtotal.toFixed(2)}</span>
              </div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Shipping</span>
                <span style={{...styles.summaryValue, color: shipping === 0 ? '#22c55e' : '#374151'}}>
                  {shipping === 0 ? '🎉 FREE' : `$${shipping.toFixed(2)}`}
                </span>
              </div>
              
              <div style={styles.totalRow}>
                <span style={styles.totalLabel}>Total</span>
                <span style={styles.totalValue}>${total.toFixed(2)}</span>
              </div>

              <button
                onClick={() => {
                  closeCart();
                  onCheckout?.();
                }}
                style={styles.checkoutButton}
              >
                🚀 Proceed to Checkout
              </button>
              
              <button onClick={closeCart} style={styles.continueShopping}>
                ← Continue Shopping
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

// Cart Item Component - Amazon-style: image left, details right, qty dropdown + delete inline
const CartItem = ({ item, index, onRemove, onUpdateQuantity }) => {
  const quantityOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  
  return (
    <div 
      className="cart-item" 
      style={{
        ...styles.item, 
        animationDelay: `${index * 0.05}s`
      }}
    >
      {/* Item Image */}
      <div style={styles.itemImageWrapper}>
        <img
          src={item.image}
          alt={item.name}
          style={styles.itemImage}
        />
        {item.petImage && (
          <img
            src={item.petImage}
            alt={item.petName}
            style={styles.petBadge}
          />
        )}
      </div>

      {/* Item Details */}
      <div style={styles.itemDetails}>
        <h4 style={styles.itemName}>{item.name}</h4>
        <p style={styles.itemVariant}>
          {item.variant && `${item.variant}`}
          {item.variant && item.size && ' • '}
          {item.size && item.size}
        </p>
        {item.petName && (
          <p style={styles.itemPetName}>🐾 {item.petName}'s portrait</p>
        )}
        
        {/* Price */}
        <span style={styles.itemPrice}>
          ${(item.price * item.quantity).toFixed(2)}
        </span>
        
        {/* Bottom Row: Qty Dropdown + Delete - Amazon style */}
        <div style={styles.itemActions}>
          <div style={styles.qtyWrapper}>
            <label style={styles.qtyLabel}>Qty:</label>
            <select
              value={item.quantity}
              onChange={(e) => onUpdateQuantity(Number(e.target.value))}
              style={styles.qtySelect}
            >
              {quantityOptions.map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
          
          <span style={styles.divider}>|</span>
          
          <button onClick={onRemove} style={styles.deleteBtn}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: 1000,
  },
  sidebar: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '480px',
    maxWidth: '100%',
    height: '100%',
    background: '#fff',
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '-8px 0 32px rgba(0,0,0,0.2)',
  },
  
  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fff',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  headerEmoji: {
    fontSize: '28px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  itemCountText: {
    fontSize: '13px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  closeButton: {
    width: '40px',
    height: '40px',
    minWidth: '40px',
    minHeight: '40px',
    maxWidth: '40px',
    maxHeight: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    color: '#fff',
    boxShadow: '0 2px 8px rgba(147, 51, 234, 0.3)',
    flexShrink: 0,
  },
  
  // Empty State
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '64px',
    opacity: 0.5,
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '8px',
  },
  emptySubtext: {
    fontSize: '15px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '28px',
  },
  shopNowBtn: {
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)',
  },
  
  // Free Shipping Banner
  freeShippingBanner: {
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)',
    borderBottom: '1px solid rgba(251, 191, 36, 0.3)',
  },
  progressBarWrapper: {
    width: '100%',
    height: '6px',
    background: 'rgba(251, 191, 36, 0.2)',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressBarFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  freeShippingText: {
    fontSize: '13px',
    color: '#92400e',
    fontFamily: FONT_FAMILY,
    margin: 0,
    textAlign: 'center',
  },
  freeShippingUnlocked: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)',
    borderBottom: '1px solid rgba(34, 197, 94, 0.3)',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#166534',
    fontFamily: FONT_FAMILY,
  },
  
  // Item List
  itemList: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 24px',
  },
  
  // Cart Item - Amazon style
  item: {
    display: 'flex',
    gap: '16px',
    padding: '16px 0',
    borderBottom: '1px solid #e5e7eb',
  },
  itemImageWrapper: {
    position: 'relative',
    width: '90px',
    height: '90px',
    flexShrink: 0,
    borderRadius: '10px',
    overflow: 'hidden',
    background: '#f5f5f5',
  },
  itemImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  petBadge: {
    position: 'absolute',
    bottom: '-2px',
    right: '-2px',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    border: '2px solid #fff',
    objectFit: 'cover',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  },
  itemDetails: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  itemName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '2px',
  },
  itemVariant: {
    fontSize: '13px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '2px',
  },
  itemPetName: {
    fontSize: '13px',
    color: '#7c3aed',
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    margin: 0,
    marginBottom: '6px',
  },
  itemPrice: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    marginBottom: '10px',
  },
  
  // Actions row - Amazon style
  itemActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: 'auto',
  },
  qtyWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  qtyLabel: {
    fontSize: '13px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
  },
  qtySelect: {
    padding: '4px 24px 4px 8px',
    fontSize: '13px',
    fontFamily: FONT_FAMILY,
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#f9fafb',
    color: '#374151',
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%236B7280' stroke-width='1.5'%3E%3Cpath d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 4px center',
    backgroundSize: '16px',
  },
  divider: {
    color: '#d1d5db',
    fontSize: '14px',
  },
  deleteBtn: {
    background: 'none',
    border: 'none',
    color: '#0066c0',
    fontSize: '13px',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    padding: 0,
  },
  
  // Summary
  summary: {
    padding: '20px 24px',
    borderTop: '2px solid #e5e7eb',
    background: '#fafafa',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  summaryLabel: {
    fontSize: '14px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
  },
  summaryValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    fontFamily: FONT_FAMILY,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: '14px',
    borderTop: '1px solid #e5e7eb',
    marginBottom: '20px',
  },
  totalLabel: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
  },
  totalValue: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
  },
  checkoutButton: {
    width: '100%',
    padding: '16px',
    fontSize: '17px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#fff',
    cursor: 'pointer',
    marginBottom: '10px',
    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  continueShopping: {
    width: '100%',
    background: 'none',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    color: '#6b7280',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    padding: '12px',
    textAlign: 'center',
  },
};

export default CartSidebar;
